import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import TabGroup from "@/components/ui/tab";
import Avatar from "@/components/ui/avatar";
import Chip from "@/components/ui/chip";
import ProgressBar from "@/components/ui/progress-bar";
import { LeftChevron } from "@/components/icons";
import {
  Book,
  MapPin,
  Calendar,
  DocumentModal,
  User as UserIcon,
  Phone,
  Sent,
  Building,
  Category as CategoryIcon,
  Eye as EyeIcon,
  Actions as ActionsIcon,
  Target,
  Employees,
  Download as DownloadIcon,
  TooltipIcon,
} from "@/components/icons";
import { InternStatus, type Intern, type InternType } from "../types/intern-management.types";
import type { TableColumn, FilterField, SortOption } from "@/types/component";
import { internManagementApi } from "../services/intern-management.api";
import cardStyles from "../components/InternDetailDrawer.module.scss";
import styles from "./InternDetailPage.module.scss";

const INTERN_TYPE_LABELS: Record<InternType, string> = {
  universiteZorunlu: i18n.t("internDetail.types.universityMandatory"),
  liseBeceri: i18n.t("internDetail.types.highSchoolSkill"),
};

const STATUS_LABELS: Record<InternStatus, string> = {
  [InternStatus.AKTIF]: i18n.t("internManagement.addModal.statusOptions.active"),
  [InternStatus.TAMAMLANDI]: i18n.t("internDetail.completed"),
};

const getInternDisplayData = (intern: Intern) => {
  const totalTrainings = 20;
  const completedCount = Math.round((intern.educationProgress / 100) * totalTrainings);
  return {
    ...intern,
    phone: "+90 545 654 56 56",
    email: "aysekaya@mail.com",
    startDate: "12.01.2026",
    endDate: "12.03.2026",
    supervisor: "Mehmet Yılmaz",
    school: "Ankara Üniversitesi",
    department: i18n.t("internDetail.mock.department"),
    class: "3",
    absenteeismPresent: 2,
    absenteeismTotal: 30,
    completedCount,
    totalTrainings,
    // Backend gerçek değer döndürmediği sürece null tutuyoruz; UI "—" gösterir.
    // Hiç eğitim almamış stajyerde %85 görünmesi mock veri izlenimi veriyordu.
    successAverage:
      completedCount > 0 && totalTrainings > 0
        ? Math.round((completedCount / totalTrainings) * 100)
        : null,
    certificateCount: 12,
  };
};

interface TrainingRow {
  id: string;
  trainingName: string;
  format: string;
  progress: number;
  status: string;
  statusType: "success" | "warning" | "error" | "info" | "default";
}

interface ExamRow {
  id: string;
  examName: string;
  score: string;
  status: string;
  statusType: "success" | "warning" | "error" | "info" | "default";
}

interface CertificateRow {
  id: string;
  name: string;
  relatedEducation: string;
  issueCount: string;
  createdAt: string;
}

interface LogRow {
  id: string;
  dateTime: string;
  title: string;
  description: string;
  badge: string;
}

const DUMMY_TRAININGS: TrainingRow[] = [
  { id: "1", trainingName: i18n.t("internDetail.mock.trainings.osgBasic"), format: i18n.t("requestAndApproval.educationTypes.faceToFace"), progress: 95, status: i18n.t("internManagement.statusFilter.inProgress"), statusType: "info" },
  { id: "2", trainingName: i18n.t("internDetail.mock.trainings.highVoltage"), format: i18n.t("requestAndApproval.educationTypes.unit"), progress: 91, status: i18n.t("internManagement.statusFilter.inProgress"), statusType: "info" },
  { id: "3", trainingName: i18n.t("internDetail.mock.trainings.kvkk"), format: i18n.t("requestAndApproval.educationTypes.unit"), progress: 46, status: i18n.t("internManagement.statusFilter.inProgress"), statusType: "info" },
  { id: "4", trainingName: i18n.t("internDetail.mock.trainings.leadership"), format: i18n.t("requestAndApproval.educationTypes.unit"), progress: 46, status: i18n.t("internManagement.statusFilter.educationFinished"), statusType: "warning" },
  { id: "5", trainingName: i18n.t("internDetail.mock.trainings.osgBasic"), format: i18n.t("requestAndApproval.educationTypes.faceToFace"), progress: 75, status: i18n.t("internManagement.statusFilter.inProgress"), statusType: "info" },
  { id: "6", trainingName: i18n.t("internDetail.mock.trainings.osgBasic"), format: i18n.t("requestAndApproval.educationTypes.faceToFace"), progress: 0, status: i18n.t("internManagement.statusFilter.notAttended"), statusType: "error" },
  { id: "7", trainingName: i18n.t("internDetail.mock.trainings.osgBasic"), format: i18n.t("requestAndApproval.educationTypes.faceToFace"), progress: 0, status: i18n.t("internManagement.statusFilter.notStarted"), statusType: "default" },
  { id: "8", trainingName: i18n.t("internDetail.mock.trainings.osgBasic"), format: i18n.t("requestAndApproval.educationTypes.online"), progress: 100, status: i18n.t("internManagement.statusFilter.completed"), statusType: "success" },
];

const DUMMY_EXAMS: ExamRow[] = [
  { id: "1", examName: i18n.t("internDetail.mock.exams.osg"), score: "85", status: i18n.t("internManagement.statusFilter.completed"), statusType: "success" },
  { id: "2", examName: i18n.t("internDetail.mock.exams.kvkk"), score: "72", status: i18n.t("internManagement.statusFilter.completed"), statusType: "success" },
  { id: "3", examName: i18n.t("internDetail.mock.exams.leadership"), score: "-", status: i18n.t("internDetail.status.pending"), statusType: "warning" },
];

const DUMMY_CERTIFICATES: CertificateRow[] = [
  { id: "1", name: i18n.t("internDetail.mock.certs.osg"), relatedEducation: i18n.t("internDetail.mock.relatedEducation.osg"), issueCount: "145", createdAt: "12.10.2025" },
  { id: "2", name: i18n.t("internDetail.mock.certs.kvkk"), relatedEducation: i18n.t("internDetail.mock.relatedEducation.kvkk"), issueCount: "89", createdAt: "05.09.2025" },
  { id: "3", name: i18n.t("internDetail.mock.certs.leadership"), relatedEducation: i18n.t("internDetail.mock.relatedEducation.leadership"), issueCount: "32", createdAt: "20.08.2025" },
];

const DUMMY_LOGS: LogRow[] = [
  { id: "1", dateTime: "20.04.2026 11:08:45", title: i18n.t("library.title"), description: i18n.t("internDetail.mock.fileDownloaded"), badge: i18n.t("logs.actionLabels.EXPORT") },
  { id: "2", dateTime: "20.04.2026 11:08:45", title: i18n.t("library.title"), description: i18n.t("internDetail.mock.fileDownloaded"), badge: i18n.t("logs.actionLabels.EXPORT") },
  { id: "3", dateTime: "19.04.2026 14:22:10", title: i18n.t("logs.entityLabels.EDUCATION"), description: i18n.t("internDetail.mock.trainingCompleted"), badge: i18n.t("internDetail.completed") },
];

const InternDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { internId } = useParams<{ internId: string }>();
  const [intern, setIntern] = useState<Intern | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [trainingFilters, setTrainingFilters] = useState<{ format?: string; status?: string }>({});

  const filteredTrainings = [...DUMMY_TRAININGS].filter((row) => {
    const matchSearch =
      !searchTerm ||
      row.trainingName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.format.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFormat = !trainingFilters.format || row.format === trainingFilters.format;
    const matchStatus = !trainingFilters.status || row.status === trainingFilters.status;
    return matchSearch && matchFormat && matchStatus;
  });
  const filteredExams = [...DUMMY_EXAMS].filter(
    (row) => !searchTerm || row.examName.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const filteredCertificates = [...DUMMY_CERTIFICATES].filter(
    (row) =>
      !searchTerm ||
      row.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.relatedEducation.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const filteredLogs = [...DUMMY_LOGS].filter(
    (row) =>
      !searchTerm ||
      row.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const pageHeaderFilters: FilterField[] =
    activeTab === 0
      ? [
          {
            name: "format",
            label: t("staffManagement.detail.cols.format"),
            type: "dropdown",
            placeholder: t("common.all"),
            options: [
              { label: t("educationManagement.formatOptions.yuzYuze"), value: "Yüz Yüze" },
              { label: t("educationManagement.formatOptions.uniteli"), value: "Üniteli" },
              { label: t("educationManagement.formatOptions.canli"), value: "Canlı" },
            ],
          },
          {
            name: "status",
            label: t("staffManagement.detail.cols.status"),
            type: "dropdown",
            placeholder: t("common.all"),
            options: [
              { label: t("internManagement.statusFilter.inProgress"), value: "Devam Ediyor" },
              { label: t("internManagement.statusFilter.completed"), value: i18n.t("internDetailFilters.completed") },
              { label: t("internManagement.statusFilter.educationFinished"), value: "Eğitim Bitti" },
              { label: t("internManagement.statusFilter.notAttended"), value: i18n.t("internDetailFilters.notAttended") },
              { label: t("internManagement.statusFilter.notStarted"), value: i18n.t("internDetailFilters.notStarted") },
            ],
          },
        ]
      : [];

  const pageHeaderSortOptions: SortOption[] = [
    { label: t("common.sortByNameAsc"), value: "name_asc" },
    { label: t("common.sortByNameDesc"), value: "name_desc" },
  ];

  const handlePageHeaderFilterChange = (values: Record<string, unknown>) => {
    setTrainingFilters({
      format: (values.format as string) || undefined,
      status: (values.status as string) || undefined,
    });
  };

  const handleExportLogs = () => {
    console.debug("export logs");
  };

  useEffect(() => {
    if (!internId) return;
    let cancelled = false;
    setLoading(true);
    internManagementApi
      .getById(internId)
      .then((res) => {
        if (!cancelled) setIntern(res.data);
      })
      .catch(() => {
        if (!cancelled) setIntern(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [internId]);

  if (loading || !intern) {
    return (
      <DashboardLayout>
        <Header title={t("internManagement.title")} />
        <div className={styles.loading}>{t("common.loading")}</div>
      </DashboardLayout>
    );
  }

  const data = getInternDisplayData(intern);
  const progressPercent = intern.educationProgress;

  const breadcrumbItems = [
    { label: t("internManagement.title"), href: "/dashboard/intern-management" },
    { label: intern.name },
  ];

  const detailTabs = [
    { label: t("internManagement.detailPage.tabs.trainings"), content: null },
    { label: t("internManagement.detailPage.tabs.exams"), content: null },
    { label: t("internManagement.detailPage.tabs.certificates"), content: null },
    { label: t("internManagement.detailPage.tabs.logs"), content: null },
  ];

  const trainingColumns: TableColumn<TrainingRow>[] = [
    { header: t("staffManagement.detail.cols.trainingName"), accessor: (r) => r.trainingName },
    { header: t("staffManagement.detail.cols.format"), accessor: (r) => r.format },
    {
      header: t("staffManagement.detail.cols.progress"),
      accessor: (r) => (
        <div className={styles.progressCell}>
          <ProgressBar value={r.progress} showLabel={false} size="small" />
          <span>%{r.progress}</span>
        </div>
      ),
    },
    {
      header: t("staffManagement.detail.cols.status"),
      accessor: (r) => (
        <Chip type={r.statusType} size="small">
          {r.status}
        </Chip>
      ),
    },
    {
      header: t("staffManagement.detail.cols.action"),
      accessor: () => (
        <button type="button" className={styles.iconButton} aria-label={t("common.view")}>
          <EyeIcon width={20} height={20} />
        </button>
      ),
    },
  ];

  const examColumns: TableColumn<ExamRow>[] = [
    { header: t("staffManagement.detail.cols.examName"), accessor: (r) => r.examName },
    { header: t("staffManagement.detail.cols.score"), accessor: (r) => r.score },
    {
      header: t("staffManagement.detail.cols.status"),
      accessor: (r) => (
        <Chip type={r.statusType} size="small">
          {r.status}
        </Chip>
      ),
    },
    {
      header: t("staffManagement.detail.cols.action"),
      accessor: () => (
        <button type="button" className={styles.iconButton} aria-label={t("common.view")}>
          <EyeIcon width={20} height={20} />
        </button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("internManagement.title")} />
      <div className={styles.breadcrumbContainer}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/dashboard/intern-management")}
          aria-label={t("common.back")}
        >
          <LeftChevron width={20} height={20} />
        </button>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className={styles.pageLayout}>
        <aside className={styles.leftColumn}>
          <div className={cardStyles.personnelCard}>
            <div className={cardStyles.personnelCardTop}>
              <div className={cardStyles.avatarWrap}>
                <Avatar alt={intern.name} color="primary" />
              </div>
              <div className={cardStyles.personnelInfo}>
                <div className={cardStyles.personnelHeader}>
                  <div className={cardStyles.personnelNameTitle}>
                    <h2 className={cardStyles.personnelName}>{intern.name}</h2>
                    <p className={cardStyles.personnelTitle}>
                      {t("internManagement.detailDrawer.role")}
                    </p>
                  </div>
                  <div className={cardStyles.badgesRow}>
                    <Chip type="success" size="small">
                      {STATUS_LABELS[intern.status]}
                    </Chip>
                  </div>
                  <button type="button" className={styles.menuButton} aria-label={t("common.more")}>
                    <ActionsIcon width={20} height={20} />
                  </button>
                </div>
              </div>
            </div>
            <div className={cardStyles.contactPills}>
              <span className={cardStyles.contactPill}>TC {intern.tc}</span>
              <span className={cardStyles.contactPill}>{data.phone}</span>
              <span className={cardStyles.contactPill}>{data.email}</span>
            </div>
            <div className={cardStyles.cardEducationProgress}>
              <h4 className={cardStyles.cardEducationProgressTitle}>
                {t("internManagement.detailDrawer.educationProgress")}
              </h4>
              <ProgressBar
                value={progressPercent}
                score={data.completedCount}
                total={data.totalTrainings}
                showLabel={false}
                size="medium"
              />
              <div className={cardStyles.progressLabels}>
                <span>
                  ({data.completedCount}/{data.totalTrainings})
                </span>
                <span className={cardStyles.progressPercent}>
                  {data.successAverage != null ? `%${data.successAverage}` : "—"}
                </span>
              </div>
            </div>

            <section className={cardStyles.section}>
              <h4 className={cardStyles.sectionTitle}>
                {t("staffManagement.personnelSummary.personalInfo")}
              </h4>
              <div className={cardStyles.sectionCard}>
                <div className={cardStyles.detailList}>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <UserIcon width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>TC</span>
                    <span className={cardStyles.detailValue}>{intern.tc}</span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Phone width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.detail.phone")}
                    </span>
                    <span className={cardStyles.detailValue}>{data.phone}</span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Sent width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.detail.email")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      <a href={`mailto:${data.email}`} className={styles.emailLink}>
                        {data.email}
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <section className={cardStyles.section}>
              <h4 className={cardStyles.sectionTitle}>
                {t("internManagement.detailDrawer.internshipInfo")}
              </h4>
              <div className={cardStyles.sectionCard}>
                <div className={cardStyles.detailList}>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Book width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("internManagement.detailDrawer.internType")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {INTERN_TYPE_LABELS[intern.internType]}
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <MapPin width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("internManagement.detailDrawer.internshipLocation")}
                    </span>
                    <span className={cardStyles.detailValue}>{intern.unitOrBusiness}</span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Calendar width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("internManagement.detailDrawer.internshipDates")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {data.startDate} - {data.endDate}
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <DocumentModal width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("internManagement.detailDrawer.absenteeism")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {data.absenteeismPresent} / {data.absenteeismTotal}
                      <TooltipIcon width={14} height={14} style={{ marginLeft: 4 }} />
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <UserIcon width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("internManagement.detailDrawer.supervisor")}
                    </span>
                    <span className={cardStyles.detailValue}>{data.supervisor}</span>
                  </div>
                </div>
              </div>
            </section>

            <section className={cardStyles.section}>
              <h4 className={cardStyles.sectionTitle}>
                {t("internManagement.detailDrawer.personalAcademicInfo")}
              </h4>
              <div className={cardStyles.sectionCard}>
                <div className={cardStyles.detailList}>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Book width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("internManagement.detailDrawer.school")}
                    </span>
                    <span className={cardStyles.detailValue}>{data.school}</span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Building width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("internManagement.detailDrawer.department")}
                    </span>
                    <span className={cardStyles.detailValue}>{data.department}</span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <CategoryIcon width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("internManagement.detailDrawer.class")}
                    </span>
                    <span className={cardStyles.detailValue}>{data.class}</span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Target width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.personnelSummary.successAverage")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {data.successAverage != null ? `%${data.successAverage}` : "—"}
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <DocumentModal width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.personnelSummary.certificateCount")}
                    </span>
                    <span className={cardStyles.detailValue}>{data.certificateCount}</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </aside>

        <div className={styles.rightColumn}>
          <TabGroup
            tabs={detailTabs}
            defaultActiveTab={activeTab}
            onTabChange={setActiveTab}
            fullWidth
          />

          <PageHeader
            title=""
            searchPlaceholder={t("common.search")}
            searchValue={searchTerm}
            onSearch={setSearchTerm}
            showSort
            sortOptions={pageHeaderSortOptions}
            filters={pageHeaderFilters}
            onFilterChange={handlePageHeaderFilterChange}
            onAddNew={activeTab === 3 ? handleExportLogs : undefined}
            addButtonLabel={activeTab === 3 ? t("common.export") : undefined}
            className={styles.pageHeader}
          />

          <div className={styles.tableSection}>
            {activeTab === 0 && (
              <Table<TrainingRow>
                columns={trainingColumns}
                data={[...filteredTrainings]}
                getRowId={(r) => r.id}
                isLoading={false}
                renderActions={() => null}
                hasVisibleActions={false}
                totalRecords={filteredTrainings.length}
                pageSize={10}
                currentPage={1}
                onPageChange={() => {
                  console.debug("page change");
                }}
                contentBasedHeight
                emptyMessage={t("staffManagement.detail.noData")}
              />
            )}
            {activeTab === 1 && (
              <Table<ExamRow>
                columns={examColumns}
                data={[...filteredExams]}
                getRowId={(r) => r.id}
                isLoading={false}
                renderActions={() => null}
                hasVisibleActions={false}
                totalRecords={filteredExams.length}
                pageSize={10}
                currentPage={1}
                onPageChange={() => {
                  console.debug("page change");
                }}
                contentBasedHeight
                emptyMessage={t("staffManagement.detail.noData")}
              />
            )}
            {activeTab === 2 && (
              <div className={styles.certificateList}>
                {filteredCertificates.length === 0 ? (
                  <p className={styles.emptyText}>
                    {t("staffManagement.detail.noData")}
                  </p>
                ) : (
                  filteredCertificates.map((cert) => (
                    <div key={cert.id} className={styles.certificateCard}>
                      <div className={styles.certificatePreview}>
                        <div className={styles.certificatePreviewInner}>
                          <span className={styles.certificatePreviewTitle}>CERTIFICATE OF APPRECIATION</span>
                          <span className={styles.certificatePreviewName}>{intern.name}</span>
                        </div>
                      </div>
                      <div className={styles.certificateDetails}>
                        <div className={styles.certificateDetailsHeader}>
                          <h3 className={styles.certificateCardTitle}>{cert.name}</h3>
                          <button type="button" className={styles.downloadBtn} aria-label={t("common.download")}>
                            <DownloadIcon width={20} height={20} />
                          </button>
                        </div>
                        <div className={styles.certificateDetailList}>
                          <div className={styles.certificateDetailItem}>
                            <Book width={20} height={20} className={styles.certDetailIcon} />
                            <span>
                              {t("staffManagement.detail.cols.relatedEducation")}: {cert.relatedEducation}
                            </span>
                          </div>
                          <div className={styles.certificateDetailItem}>
                            <Employees width={20} height={20} className={styles.certDetailIcon} />
                            <span>
                              {t("staffManagement.detail.cols.issueCount")}: {cert.issueCount}
                            </span>
                          </div>
                          <div className={styles.certificateDetailItem}>
                            <Calendar width={20} height={20} className={styles.certDetailIcon} />
                            <span>
                              {t("staffManagement.detail.cols.createdAt")}: {cert.createdAt}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            {activeTab === 3 && (
              <div className={styles.logTimeline}>
                {filteredLogs.length === 0 ? (
                  <p className={styles.emptyText}>
                    {t("staffManagement.detail.noData")}
                  </p>
                ) : (
                  <>
                    <div className={styles.logTimelineLine} aria-hidden />
                    {filteredLogs.map((log) => (
                      <div key={log.id} className={styles.logEntry}>
                        <span className={styles.logTime}>{log.dateTime}</span>
                        <div className={styles.logDotWrap}>
                          <div className={styles.logDot} />
                        </div>
                        <div className={styles.logCard}>
                          <div className={styles.logCardContent}>
                            <h4 className={styles.logCardTitle}>{log.title}</h4>
                            <p className={styles.logCardDescription}>{log.description}</p>
                          </div>
                          <Chip type="info" size="small">
                            {log.badge}
                          </Chip>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InternDetailPage;
